import { AnalyticsService } from '@core/services';
import { HeroContent } from '@core/services/site-content/site-content.types';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, property, unsafeCSS } from '@rxdi/lit-html';
import { rich } from '@shared/rich-text';
import { ScrollToService } from '@shared/services/scroll/scroll-to.service';
import { ArrowIcon, StudioStyles } from '@shared/styles/studio.styles';
import * as ScrollbarCSS from 'bundle-text:../../shared/styles/scrollbar.css';

import { MarblesComponent } from './marbles.component';

/** Home: the opening section. */
@Component({
  selector: 'rs-hero',
  components: [MarblesComponent],
  styles: [
    StudioStyles,
    unsafeCSS(ScrollbarCSS),
    css`
      .hero {
        position: relative;
        min-height: 100vh;
        padding: clamp(130px, 18vh, 190px) 0 48px;
        overflow: hidden;
        isolation: isolate;
      }

      .aura {
        position: absolute;
        inset: -20% -10% auto -10%;
        height: 90%;
        z-index: -1;
        background: radial-gradient(40% 50% at 18% 30%, var(--rs-plasma-soft), transparent 70%),
          radial-gradient(35% 45% at 85% 20%, var(--rs-ion-soft), transparent 70%),
          radial-gradient(30% 40% at 60% 70%, var(--rs-signal-soft), transparent 70%);
        filter: blur(20px);
        animation: drift 18s ease-in-out infinite alternate;
      }

      @keyframes drift {
        to {
          transform: translate3d(2%, 3%, 0) scale(1.05);
        }
      }

      .top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
      }

      .status {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        height: 34px;
        padding: 0 14px;
        border-radius: 999px;
        border: 1px solid var(--rs-line-strong);
        background: var(--rs-surface-glass);
        font-size: 13px;
        color: var(--rs-text-2);
      }

      .pulse {
        position: relative;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--rs-signal);
      }

      .pulse::after {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        border: 1px solid var(--rs-signal);
        animation: ping 2s var(--rs-ease) infinite;
      }

      @keyframes ping {
        from {
          transform: scale(0.6);
          opacity: 1;
        }
        to {
          transform: scale(2.2);
          opacity: 0;
        }
      }

      h1.display {
        font-size: clamp(56px, 11.5vw, 168px);
        margin-top: clamp(28px, 5vh, 56px);
      }

      h1 .line {
        display: block;
        overflow: hidden;
        padding-bottom: 0.08em;
        margin-bottom: -0.08em;
      }

      h1 .line > span {
        display: inline-block;
        animation: rise 1s var(--rs-ease) both;
      }

      h1 .line:nth-child(2) > span {
        animation-delay: 0.12s;
      }

      @keyframes rise {
        from {
          transform: translateY(105%);
        }
        to {
          transform: translateY(0);
        }
      }

      .intro {
        display: grid;
        gap: 32px;
        margin-top: clamp(28px, 4vh, 44px);
        animation: fade 1s 0.35s var(--rs-ease) both;
      }

      @media (min-width: 960px) {
        .intro {
          grid-template-columns: 1fr auto;
          align-items: end;
        }
      }

      .intro .lead {
        margin: 0;
        max-width: 54ch;
      }

      .intro .lead strong {
        color: var(--rs-text);
        font-weight: 600;
      }

      .ctas {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
      }

      .diagram {
        margin-top: clamp(40px, 7vh, 72px);
        padding: clamp(18px, 3vw, 32px);
        animation: fade 1.2s 0.55s var(--rs-ease) both;
      }

      /* phones: keep the diagram readable and let it be swiped */
      .diagram-scroll {
        overflow-x: auto;
      }

      .diagram-scroll rs-marbles {
        min-width: 680px;
      }

      @media (min-width: 760px) {
        .diagram-scroll rs-marbles {
          min-width: 0;
        }
      }

      .diagram-head {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 18px;
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-text-muted);
      }

      .facts {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1px;
        margin-top: 24px;
        border-radius: var(--rs-radius-lg);
        overflow: hidden;
        background: var(--rs-line);
        border: 1px solid var(--rs-line);
        animation: fade 1.2s 0.7s var(--rs-ease) both;
      }

      @media (min-width: 960px) {
        .facts {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      .fact {
        background: var(--rs-ink-2);
        padding: 22px 24px;
      }

      .fact b {
        display: block;
        font-family: var(--rs-font-display);
        font-size: clamp(28px, 3vw, 40px);
        letter-spacing: -0.03em;
        color: var(--rs-text);
      }

      .fact span {
        display: block;
        margin-top: 6px;
        font-size: 13px;
        line-height: 1.45;
        color: var(--rs-text-muted);
      }

      @keyframes fade {
        from {
          opacity: 0;
          transform: translateY(16px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      @media (max-width: 640px) {
        .diagram-head span:last-child {
          display: none;
        }
      }
    `,
  ],
  template(this: HeroComponent) {
    const c = this.content;
    if (!c) {
      return html``;
    }
    return html`
      <section class="hero">
        <div class="aura" aria-hidden="true"></div>
        <div class="grid-bg" aria-hidden="true"></div>
        <div class="wrap">
          <div class="top">
            <span class="kicker">${c.kicker}</span>
            <span class="status"><span class="pulse"></span>${c.status}</span>
          </div>

          <h1 class="display">
            <span class="line"><span>${rich(c.headlineTop)}</span></span>
            <span class="line"><span>${rich(c.headlineBottom)}</span></span>
          </h1>

          <div class="intro">
            <p class="lead">${rich(c.lead)}</p>
            <div class="ctas">
              <a class="btn btn-signal" href="#contact" @click=${(e: Event) => this.go(e, 'rs-contact', 'hero_start')}>
                ${c.primaryCta} ${ArrowIcon}
              </a>
              <a class="btn btn-ghost" href="#work" @click=${(e: Event) => this.go(e, 'rs-work', 'hero_work')}>
                ${c.secondaryCta}
              </a>
            </div>
          </div>

          <div class="card diagram">
            <div class="diagram-head">
              <span>// how we work, as a stream</span>
              <span>rxjs marble diagram</span>
            </div>
            <div class="diagram-scroll neat-scroll"><rs-marbles></rs-marbles></div>
          </div>

          <div class="facts">
            ${c.facts.map((f) => html`<div class="fact"><b>${f.value}</b><span>${f.label}</span></div>`)}
          </div>
        </div>
      </section>
    `;
  },
})
export class HeroComponent extends LitElement {
  @property({ attribute: false }) content: HeroContent;

  @Inject(ScrollToService)
  private scroller: ScrollToService;

  @Inject(AnalyticsService)
  private analytics: AnalyticsService;

  private go(event: Event, section: string, action: string) {
    event.preventDefault();
    this.analytics.track('cta_click', { action });
    this.scroller.scrollToElement(section);
  }
}
